// packages/web/src/components/teach/AssignModal.tsx
import React, { useState, useEffect } from 'react';
import { useTeacherLearningStore } from '../../stores/teacherLearningStore';
import { teacherLearningApi } from '../../services/teacherLearningApi';
import { apiService } from '@shared/services/api/ApiService';
import { useStore } from '../../stores';
import { familyApi, FamilyChild } from '../../services/familyApi';
import { buildStaticUrl } from '../../config/env';
import { Toast } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

export const AssignModal: React.FC = () => {
  const {
    exercises,
    assignMode,
    selectedClasses,
    selectedStudents,
    setShowAssignModal,
    setAssignMode,
    setSelectedClasses,
    setSelectedStudents,
    setErrorMessage,
  } = useTeacherLearningStore();

  const [isProcessing, setIsProcessing] = useState(false);
  const [classes, setClasses] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast, success, error, hideToast } = useToast();
  const { user } = useStore();
  const isParent = user?.role === 'parent';
  const [familyChildren, setFamilyChildren] = useState<FamilyChild[]>([]);
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([]);
  
  // 作业信息
  const [homeworkName, setHomeworkName] = useState('');
  const [homeworkDescription, setHomeworkDescription] = useState('');
  const [dueDate, setDueDate] = useState('');

  // 家长模式：加载家庭子女
  useEffect(() => {
    if (isParent) {
      familyApi.getOverview().then(data => {
        setFamilyChildren(data.children);
      }).catch(() => {});
    }
  }, [isParent]);

  // 加载班级列表
  useEffect(() => {
    if (isParent) return;
    const loadClasses = async () => {
      try {
        const response = await apiService.get('/teach/classes');
        if (response.success && response.data) {
          // response.data is already the array of classes
          setClasses(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Failed to load classes:', error);
      }
    };

    if (assignMode === 'class') {
      loadClasses();
    }
  }, [assignMode]);

  // 加载学生列表
  useEffect(() => {
    if (isParent) return;
    const loadStudents = async () => {
      try {
        const response = await apiService.get('/teach/students');
        if (response.success && response.data) {
          // response.data is already the array of students
          setStudents(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Failed to load students:', error);
      }
    };

    if (assignMode === 'student') {
      loadStudents();
    }
  }, [assignMode]);

  // 处理关闭
  const handleClose = () => {
    setShowAssignModal(false);
    setSelectedClasses([]);
    setSelectedStudents([]);
    setSelectedChildIds([]);
    setSearchQuery('');
    setHomeworkName('');
    setHomeworkDescription('');
    setDueDate('');
  };

  // 处理班级选择
  const handleClassToggle = (classId: string) => {
    if (selectedClasses.includes(classId)) {
      setSelectedClasses(selectedClasses.filter((id) => id !== classId));
    } else {
      setSelectedClasses([...selectedClasses, classId]);
    }
  };

  // 处理学生选择
  const handleStudentToggle = (studentId: string) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter((id) => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  // 处理布置作业
  const handleAssign = async () => {
    // 验证作业信息
    if (!homeworkName.trim()) {
      error('请输入作业名称');
      return;
    }
    
    if (homeworkName.length > 200) {
      error('作业名称不能超过200个字符');
      return;
    }
    
    if (!dueDate) {
      error('请选择截止日期');
      return;
    }
    
    const selectedExercises = exercises.filter((ex) => ex.selected);
    const templateIds = selectedExercises.map((ex) => ex.topicId).filter(Boolean) as string[];

    if (templateIds.length === 0) {
      error('没有可布置的题目（需要先保存为模板）');
      return;
    }

    let studentIds: string[] = [];
    let classIds: string[] = [];

    if (isParent) {
      // 家长模式：直接使用选中的子女ID
      if (selectedChildIds.length === 0) {
        error('请选择至少一个家庭成员');
        return;
      }
      studentIds = selectedChildIds;
    } else if (assignMode === 'class') {
      if (selectedClasses.length === 0) {
        error('请选择至少一个班级');
        return;
      }
      
      classIds = selectedClasses;
      
      // 获取所有选中班级的学生
      setIsProcessing(true);
      try {
        const allStudentIds: string[] = [];
        for (const classId of selectedClasses) {
          const response = await apiService.get(`/teach/classes/${classId}/members`);
          if (response.success && response.data) {
            const members = Array.isArray(response.data) ? response.data : [];
            allStudentIds.push(...members.map((m: any) => m.id));
          }
        }
        studentIds = [...new Set(allStudentIds)]; // Remove duplicates
      } catch (err: any) {
        error('获取班级学生失败');
        setIsProcessing(false);
        return;
      }
    } else {
      if (selectedStudents.length === 0) {
        error('请选择至少一个学生');
        return;
      }
      studentIds = selectedStudents;
    }

    if (studentIds.length === 0) {
      error('没有找到学生');
      setIsProcessing(false);
      return;
    }

    setIsProcessing(true);

    try {
      const response = await teacherLearningApi.assignExercisesAsHomework({
        name: homeworkName,
        description: homeworkDescription,
        dueDate,
        templateIds,
        classIds,
        studentIds,
      });

      if (response.success && response.data) {
        success(
          `作业布置成功！成功：${response.data.successCount}，失败：${response.data.failedCount}`
        );
        setTimeout(() => handleClose(), 1500);
      } else {
        throw new Error(response.error?.message || '布置失败');
      }
    } catch (err: any) {
      error(err.message || '布置作业失败');
      setErrorMessage(err.message || '布置作业失败');
    } finally {
      setIsProcessing(false);
    }
  };

  // 过滤学生
  const filteredStudents = students.filter((student) =>
    student.nickname?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">布置作业</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* 内容 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* 作业信息字段（无标题） */}
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                作业名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={homeworkName}
                onChange={(e) => setHomeworkName(e.target.value)}
                placeholder="请输入作业名称"
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-2">作业描述</label>
              <textarea
                value={homeworkDescription}
                onChange={(e) => setHomeworkDescription(e.target.value)}
                placeholder="请输入作业描述（可选）"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                截止日期 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* 分隔线 */}
          <div className="border-t border-gray-200 my-6"></div>

          {isParent ? (
            /* 家长模式：直接显示家庭子女 */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                选择家庭成员 ({selectedChildIds.length})
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {familyChildren.map((child) => (
                  <label key={child.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedChildIds.includes(child.id)}
                      onChange={() => {
                        setSelectedChildIds(prev =>
                          prev.includes(child.id)
                            ? prev.filter(id => id !== child.id)
                            : [...prev, child.id]
                        );
                      }}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div className="ml-3 flex items-center gap-2">
                      {child.avatar_url ? (
                        <img src={buildStaticUrl(child.avatar_url)} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                          {child.nickname?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <span className="text-sm text-gray-700">{child.nickname}</span>
                    </div>
                  </label>
                ))}
                {familyChildren.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">暂无家庭成员</p>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* 模式选择 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">分配方式</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={assignMode === 'class'}
                      onChange={() => setAssignMode('class')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">按班级</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={assignMode === 'student'}
                      onChange={() => setAssignMode('student')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">按学生</span>
                  </label>
                </div>
              </div>

              {/* 班级选择 */}
              {assignMode === 'class' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    选择班级 ({selectedClasses.length})
                  </label>
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {classes.map((cls) => (
                      <label key={cls.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedClasses.includes(cls.id)}
                          onChange={() => handleClassToggle(cls.id)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="ml-3 text-sm text-gray-700">
                          {cls.name} ({cls.memberCount || cls.student_count || 0}人)
                        </span>
                      </label>
                    ))}
                    {classes.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">暂无班级</p>
                    )}
                  </div>
                </div>
              )}

              {/* 学生选择 */}
              {assignMode === 'student' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    选择学生 ({selectedStudents.length})
                  </label>
                  <input
                    type="text"
                    placeholder="搜索学生..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {filteredStudents.map((student) => (
                      <label key={student.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => handleStudentToggle(student.id)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="ml-3 text-sm text-gray-700">{student.nickname}</span>
                      </label>
                    ))}
                    {filteredStudents.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        {searchQuery ? '未找到匹配的学生' : '暂无学生'}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 底部 */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleAssign}
            disabled={isProcessing}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {isProcessing ? '处理中...' : '确认布置'}
          </button>
        </div>
      </div>

      {/* Toast提示 */}
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
